/**
 * Campaign-year configuration for the Event Reports page (/reports and
 * /newreports, both rendering src/views/reports).
 *
 * Two generations of report live behind that page:
 *
 *   2024  - the legacy path. Reports are built by hand-assembling a query
 *           string for the `getReportsLatest` Cloud Function, and the filters
 *           come from the Event theme/category/type dropdowns.
 *   2025+ - the API path. `getCtuReport2025` serves CTU & Clean Public Spaces
 *           and `getEventTypeReport` serves one Report Events theme at a time;
 *           both take the campaign year as an `event_year` query parameter.
 *
 * Adding 2026 therefore meant passing `event_year` through rather than writing
 * a third path -- `isApiReportYear` is what the report components branch on,
 * in place of the `year === 2025` checks they used to carry.
 */

// Newest first: this is also the left-to-right order of the year tabs.
export const REPORT_YEARS = [2026, 2025, 2024];

// Years served by getCtuReport2025 / getEventTypeReport.
const API_REPORT_YEARS = [2026, 2025];

export const DEFAULT_REPORT_YEAR = 2026;

export const isApiReportYear = (year) => API_REPORT_YEARS.includes(year);

/**
 * TEMPORARY (2026-09-17): show one campaign year only.
 *
 * While this holds a year, /reports renders just that year's tab and the Year
 * dropdowns on the CTU and CPS status pages are disabled at the current
 * campaign. Nothing below the UI changes -- REPORT_YEARS is untouched, every
 * endpoint still serves every year, and asking for 2024 or 2025 directly still
 * works. This only limits what the screens offer.
 *
 * TO REVERT: set this to null. That restores all three year tabs and re-enables
 * both dropdowns; no other edit is needed.
 */
export const UI_LOCKED_YEAR = 2026;

/**
 * Year tabs to render on /reports -- the full list when nothing is locked.
 *
 * Kept separate from REPORT_YEARS so the tab strip is the only thing narrowed:
 * REPORT_YEARS stays the canonical list of campaigns the page can handle.
 */
export const VISIBLE_REPORT_YEARS =
  UI_LOCKED_YEAR === null ? REPORT_YEARS : REPORT_YEARS.filter((year) => year === UI_LOCKED_YEAR);

// The Event Theme Name dropdown's first entry. Not a real event_theme_id: it
// selects the getCtuReport2025 report (CTU events, i.e. event_name_id 34, plus
// Clean Public Spaces, i.e. event_theme_id 7) instead of getEventTypeReport.
export const CTU_CPS_THEME_OPTION = { name: 'CTU & Clean Public Spaces', value: 'all' };

// 2026 needs a longer label for the same option. Pillar 1 is named
// 'Transformation of Cleanliness Target Units (CTUs) & Clean Public Spaces',
// which reads as the same thing, but the two hit different tables (events vs
// report_events) and return entirely different metrics -- this one the CTU/CPS
// pipeline counts, Pillar 1 the SHS-2026 indicators. The suffix names the
// columns this option actually returns so the two cannot be confused.
export const CTU_CPS_THEME_OPTION_2026 = {
  name: 'CTU & Clean Public Spaces (Identified & Transformed)',
  value: 'all',
};

/**
 * Themes stored in the `report_events` table rather than `events`.
 *
 * These are reported through the Report Events form, so their rows are fetched
 * with getAllEventsByQueryOther and they carry none of the per-event columns
 * (CTU name, coordinator, start date, completion) that the event table has.
 */
export const REPORT_EVENT_THEME_IDS = [3, 5, 6, 8, 9, 10, 11, 12];

export const isReportEventTheme = (themeId) => REPORT_EVENT_THEME_IDS.includes(Number(themeId));

/**
 * The 2026 Event Theme Name options, mirroring PILLAR_THEME_OPTIONS in
 * src/views/report-event/index.js -- the same seven entries in the same order,
 * because a theme can only be reported on if that form can report it.
 *
 * Pillars 3 and 4 reuse the existing themes 3 and 6; the rest carry ids
 * inserted into EVENT_THEME_MASTER on 2026-09-07. Theme 5 (Clean Green Utsav)
 * is 2025-only and deliberately absent.
 */
export const PILLAR_THEME_OPTIONS_2026 = [
  {
    value: 8,
    name: 'Pillar 1: Transformation of Cleanliness Target Units (CTUs) & Clean Public Spaces',
  },
  { value: 9, name: 'Pillar 2: Swachh Paathshala: Yuva for Swachhata' },
  { value: 3, name: 'Pillar 3: SafaiMitra Suraksha Evam Samman Shivir' },
  { value: 6, name: 'Pillar 4: Jan Bhagidari for Swachhata' },
  { value: 10, name: 'Pillar 5: Swachhata se Samriddhi' },
  { value: 12, name: 'September 27: World Tourism Day' },
  { value: 11, name: 'October 2: Swachh Bharat Diwas' },
];

/**
 * The 2025 Event Theme Name options, by id.
 *
 * These are the only three themes with a 2025 indicator set: SafaiMitra
 * Suraksha Shivirs (3), Clean Green Utsav (5) and Advocacy (6). They match the
 * templates getEventTypeReport serves for 2025 and the per-theme header blocks
 * in both report components.
 *
 * This replaces the old `!== 1 && !== 2 && !== 4 && !== 7` blacklist. A
 * blacklist was fine while the master table held eight rows, but the SHS-2026
 * pillars (8-12) were inserted on 2026-09-07 and immediately leaked into the
 * 2025 dropdown -- picking one asked the API for, say, theme 9 in 2025, which
 * matches no rows and has no column list, so the table came back empty with no
 * error. A whitelist cannot go wrong that way when the next theme lands.
 */
export const REPORT_THEME_IDS_2025 = [3, 5, 6];

/**
 * Build the Event Theme Name options for a campaign year.
 *
 * Both years intersect their own list with EVENT_THEME_MASTER, so a theme is
 * only offered once its master row exists. 2026 takes its labels and ordering
 * from PILLAR_THEME_OPTIONS_2026 (the KPI document's wording); 2025 takes them
 * from the master table, as that dropdown always has, and sorts alphabetically.
 *
 * @param {number} year          campaign year
 * @param {Array}  masterThemes  EVENT_THEME_MASTER rows, from getAllEventTheme
 */
export const getThemeOptions = (year, masterThemes = []) => {
  const masterIds = new Set((masterThemes || []).map((theme) => theme.event_theme_id));

  if (year === 2026) {
    return [
      CTU_CPS_THEME_OPTION_2026,
      ...PILLAR_THEME_OPTIONS_2026.filter((option) => masterIds.has(option.value)),
    ];
  }

  const themes = (masterThemes || [])
    .filter((theme) => REPORT_THEME_IDS_2025.includes(theme.event_theme_id))
    .map((theme) => ({ name: theme.event_theme_name, value: theme.event_theme_id }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return [CTU_CPS_THEME_OPTION, ...themes];
};

/**
 * Report columns per campaign year and event theme, as `{ column: label }` in
 * display order.
 *
 * Only themes whose columns the report page cannot work out for itself are
 * listed. Theme 3 is absent from 2026 on purpose: its indicators are unchanged
 * from 2025, so it keeps using the existing SafaiMitra column handling in both
 * report components.
 *
 * The keys must match the aliases SELECTed by getEventTypeReport
 * (backend/src/functions/getEventTypeReport.js); the two lists were written
 * from the same source, REPORT_EVENT_INDICATOR_KEYS.md.
 */
export const EVENT_TYPE_REPORT_COLUMNS = {
  2026: {
    // Pillar 1: Transformation of CTUs & Clean Public Spaces
    8: {
      swachh_bazaar_advocacy_drives: 'Swachh Bazaar: Advocacy Drives',
      swachh_bazaar_areas_covered: 'Swachh Bazaar: Market Areas Covered',
      swachh_bazaar_total_participants: 'Swachh Bazaar: Participants',
      swachh_mohalla_cleanliness_drives: 'Swachh Mohalla: Cleanliness Drives',
      swachh_mohalla_bwg_registration_drives: 'Swachh Mohalla: BWG Registration Drives',
      swachh_mohalla_rwas_engaged: 'Swachh Mohalla: RWAs/RLBs Engaged',
      clean_toilets_cleaned_maintained: 'Clean Toilets: CT/PT/CSC Cleaned & Maintained',
      clean_toilets_complaints_resolved: 'Clean Toilets: Complaints Resolved',
      clean_toilets_awareness_drives: 'Clean Toilets: Awareness Drives',
      clean_toilets_total_participants: 'Clean Toilets: Participants',
      clean_toilets_reviewed_for_maintenance: 'Clean Toilets: Reviewed for O&M',
      swachh_offices_cleanliness_drives: 'Swachh Offices: Cleanliness Drives',
      swachh_offices_adopting_segregation: 'Swachh Offices: Adopting 4-Stream Segregation',
      swachh_offices_releasing_advisories: 'Swachh Offices: Advisories Released',
      clean_water_bodies_cleanliness_drives: 'Clean Water Bodies: Cleanliness Drives',
      clean_water_bodies_total_participants: 'Clean Water Bodies: Participants',
      clean_water_bodies_rejuvenation_activities: 'Clean Water Bodies: Rejuvenation Activities',
    },
    // Pillar 2: Swachh Paathshala: Yuva for Swachhata
    9: {
      paathshala_awareness_activities: 'Awareness/Demonstration Activities',
      paathshala_stakeholders_political_leadership: 'Stakeholders: Political Leadership',
      paathshala_stakeholders_school_staff: 'Stakeholders: School Staff & Principals',
      paathshala_stakeholders_csos_ngos: 'Stakeholders: CSOs/NGOs/Experts',
      paathshala_school_children_participated: 'School Children Participated',
      paathshala_rrr_drives: 'RRR Drives in Schools/Colleges',
      jal_sanchay_sessions_conducted: 'Jal Sanchay Samvad: Sessions Conducted',
      jal_sanchay_students_participated: 'Jal Sanchay Samvad: Students Participated',
      gyan_yatra_visits_conducted: 'Gyan Yatra: Visits Conducted',
      gyan_yatra_students_participated: 'Gyan Yatra: Students Participated',
      paathshala_competitions_organized: 'Swachhata Competitions Organized',
      paathshala_competitions_participants_recognized: 'Participants Recognized',
    },
    // Pillar 4: Jan Bhagidari for Swachhata. Shares theme 6 with the 2025
    // Advocacy report, hence the year-keyed lookup -- the two have no columns
    // in common. The first entry belongs to the National Campaign Roll-out
    // block, which the Report Events form also files under theme 6.
    6: {
      national_campaign_households_participated: 'National Campaign Roll-out: Households',
      ab_do_nahi_chaar_awareness_events: 'Ab Do Nahi Chaar: Awareness Events',
      ab_do_nahi_chaar_political_leadership: 'Ab Do Nahi Chaar: Political Leadership Involved',
      ab_do_nahi_chaar_total_participants: 'Ab Do Nahi Chaar: Participants',
      composting_awareness_drives: 'Composting: Awareness Drives',
      composting_distribution_drives: 'Composting: Distribution Drives',
      composting_stakeholders_distributed: 'Composting: Stakeholders Served',
      say_no_plastics_selfies_uploaded: 'Say No to Plastics: Selfies Uploaded',
      say_no_plastics_bag_kiosks: 'Say No to Plastics: Bag Kiosks',
      neki_ki_deewar_rejuvenation_drives: 'Neki Ki Deewar: Rejuvenation Drives',
      neki_ki_deewar_centres_rejuvenated: 'Neki Ki Deewar: Centres Rejuvenated',
      neki_ki_deewar_orgs_engaged: 'Neki Ki Deewar: Organisations Engaged',
      neki_ki_deewar_collection_drives: 'Neki Ki Deewar: Collection Drives',
      festivities_visarjan_cleanliness_drives: 'Clean Green Festivities: Visarjan Drives',
      festivities_flower_waste_composted: 'Clean Green Festivities: Flower Waste Composted',
      festivities_total_participants: 'Clean Green Festivities: Participants',
      sports_swachhata_competitions: 'Sports for Swachhata: Competitions',
      sports_swachhata_total_participants: 'Sports for Swachhata: Participants',
    },
    // Pillar 5: Swachhata se Samriddhi. samriddhi_competition_results is free
    // text, so it has no aggregate and no column here.
    10: {
      samriddhi_art_colleges_engaged: 'Art Colleges/Institutions Engaged',
      samriddhi_recycling_melas: 'Plastic Recycling Melas',
      samriddhi_plastic_waste_units_assessed: 'PWMUs Assessed',
      samriddhi_entries_kabaad_se_jugaad: 'Entries: Kabaad Se Jugaad',
      samriddhi_entries_kabaad_se_kala: 'Entries: Kabaad Se Kala',
    },
    // October 2: Swachh Bharat Diwas. The Gram Sabha columns are reported by
    // rural bodies and the Council Meeting columns by urban ones, so a row will
    // normally have values in one group and zeroes in the other.
    11: {
      gram_sabhas_conducted: 'Gram Sabhas Conducted',
      gram_sabhas_aankalan_approved: 'Gram Sabhas: Aankalan Validated & Approved',
      gram_sabhas_swm_rules_sensitization: 'Gram Sabhas: SWM Rules Sensitization',
      council_meetings_conducted: 'Council Meetings Conducted',
      council_meetings_swm_rules_sensitization: 'Council Meetings: SWM Rules Sensitization',
      council_meetings_segregation_sensitization: 'Council Meetings: Segregation Sensitization',
      sbd_government_staff: 'Participants: Government Staff',
      sbd_public_participation: 'Participants: Public',
      sbd_trees_planted: "Trees Planted ('Ek Ped Maa Ke Naam')",
      sbd_activities_public_places: 'Activities in Public Places',
      sbd_activities_old_age_homes: 'Activities in Old Age Homes',
      sbd_old_age_homes_engaged: 'Old Age Homes Engaged',
    },
    // September 27: World Tourism Day
    12: {
      tourism_day_areas_cleaned: 'High Footfall Areas/Destinations Cleaned',
      tourism_day_sites_illuminated: 'Sites/Monuments Illuminated',
      tourism_day_light_sound_shows: 'Light & Sound Shows Conducted',
      tourism_day_statues_cleaned: 'Public Statues Cleaned',
      tourism_day_advocacy_drives: 'Advocacy Drives (4-Way Segregation)',
      tourism_day_total_participants: 'Participants Engaged',
      tourism_day_circuits_developed: 'Tourist Circuits/Places Developed',
    },
  },
};

/**
 * The fixed column set for a year/theme, or null when the report page should
 * fall back to its own header handling (every 2025 theme, and theme 3 in 2026).
 */
export const getEventTypeReportColumns = (year, themeId) => {
  if (themeId === undefined || themeId === null || themeId === 'all') return null;
  return EVENT_TYPE_REPORT_COLUMNS[year]?.[Number(themeId)] || null;
};
